import { FC, useCallback, useState } from 'react';
import { getEasyLayout, sendServiceRequest } from '@resistdesign/voltra/app/utils';
import styled from 'styled-components';
import { APP_ENV_CONFIG } from '../config';
import { LEGAL_DOCUMENTS } from '../LegalDocuments';

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

const LayoutBase = styled.div`
  width: 100%;
  height: 100%;
`;
const AreaBase = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const {
  layout: Layout,
  areas: { CenterContent },
} = getEasyLayout(LayoutBase, AreaBase)`
  center-content, 1fr
  \\ 1fr
`;
const Card = styled.section`
  width: min(100%, 36em);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1em;
  text-align: center;
`;
const ContinueButton = styled.button`
  font-size: 1.1em;
  padding: 0.9em 1.2em;
`;
const LegalLinks = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1em;
`;
const ErrorMessage = styled.p`
  margin: 0;
`;

export type EmployerCheckoutProps = {
  authorization: string;
};

export const EmployerCheckout: FC<EmployerCheckoutProps> = ({ authorization }) => {
  const [loading, setLoading] = useState(false);
  const [redirectError, setRedirectError] = useState(false);
  const onContinueToStripe = useCallback(async () => {
    setLoading(true);
    setRedirectError(false);

    try {
      const checkoutUrl = await sendServiceRequest(
        {
          protocol: 'https',
          domain: API_DOMAIN,
          basePath: '/stripe',
          authorization,
        },
        '/create-employer-checkout'
      );

      if (typeof checkoutUrl !== 'string' || !checkoutUrl) {
        setRedirectError(true);
        return;
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      setRedirectError(true);
    } finally {
      setLoading(false);
    }
  }, [authorization]);

  return (
    <Layout>
      <CenterContent>
        <Card>
          <h2>Employer Account</h2>
          <p>Continue to Stripe to review and start your employer subscription.</p>
          <ContinueButton className="primary" onClick={onContinueToStripe} disabled={loading} aria-busy={loading}>
            {loading ? 'Preparing Stripe...' : 'Continue to Stripe'}
          </ContinueButton>
          <LegalLinks>
            {LEGAL_DOCUMENTS.map(({ label, url }) => (
              <a key={url} href={url} target="_blank" rel="noreferrer">
                {label}
              </a>
            ))}
          </LegalLinks>
          {redirectError ? (
            <ErrorMessage role="alert">
              We could not start your payment. Please contact support through{' '}
              <a href="https://advisorinterview.com/">AdvisorInterview.com</a>.
            </ErrorMessage>
          ) : undefined}
        </Card>
      </CenterContent>
    </Layout>
  );
};
